import bigcommerceGenericDefaults from '../bigcommerce-generic/config.js';

const baseFitmentFields = window.Convermax.config?.baseFitmentFields || ['Year', 'Make', 'Model'];

const categorySelectionPageUrl = '/categories/';
const brandSelectionPageUrl = '/brands-list/';

const isBrandSelectionPage = !!window.document.querySelector('#cm-brand-list');

let landingVehicleChecked = false;

function getLocalPreselection(pageType, defaults) {
  return [
    ...(defaults.getter(pageType) ?? []),
    ...(isBrandSelectionPage ? [{ field: 'FilterByVehicle', term: 'False' }] : []),
  ];
}

// PDP recommendation blocks.
// `category` mixes the real taxonomy (Bumpers>Rear Bumpers>Mud Flaps) with every
// "Shop By>Make/Model" landing category a product appears in - close to 200 values on a typical
// product - so selecting all of them would match half the catalog. We only ever pick from the
// taxonomy branch. Around 10% of products live in "Shop By" alone; those fall back to the brand.
function getMerchandisingCategories(itemData) {
  const categories = Array.isArray(itemData.category) ? itemData.category : [itemData.category];

  return categories.filter((category) => !!category && !category.startsWith('Shop By'));
}

function getDeepestCategory(itemData) {
  return getMerchandisingCategories(itemData).reduce(
    (deepest, category) =>
      category.split('>').length > (deepest?.split('>').length ?? 0) ? category : deepest,
    null,
  );
}

function getBrandSelection(itemData) {
  return itemData.brand_name ? [{ field: 'brand_name', term: itemData.brand_name }] : [];
}

// Narrowest category the product sits in, so the block stays on-topic.
function getRelatedProductsSelection(itemData) {
  const category = getDeepestCategory(itemData);

  return category ? [{ field: 'category', term: category }] : getBrandSelection(itemData);
}

// One level up from the product's own category, so this surfaces adjacent parts instead of
// repeating the Related Products block. Without a taxonomy category it degrades to the
// store-wide popular items, which is still a reasonable "you might like".
function getYouMightLikeSelection(itemData) {
  const path = getDeepestCategory(itemData)?.split('>') ?? [];
  const category = path.slice(0, Math.max(path.length - 1, 1)).join('>');

  return category ? [{ field: 'category', term: category }] : [];
}

function responseHandler(response) {
  window.Convermax.vehicleLandingImage = response.Extra?.vehicleLandingImage || '';

  const { landingMakes, landingModels } = response.Extra ?? {};
  const vehicle = window.Convermax.getVehicle?.() || {};

  if (!landingVehicleChecked && (landingMakes?.length || landingModels?.length)) {
    landingVehicleChecked = true;

    const makeMismatch = landingMakes?.length && vehicle.Make && !landingMakes.includes(vehicle.Make);
    const modelMismatch = landingModels?.length && vehicle.Model && !landingModels.includes(vehicle.Model);

    if (makeMismatch || modelMismatch) {
      setTimeout(() => window.Convermax.discardVehicle(), 0);
    }
  }

  return response;
}

export default {
  includes: ['bigcommerce-generic'],
  ...bigcommerceGenericDefaults,
  defaultView: 'grid',
  SearchRequestDefaults: {
    ...bigcommerceGenericDefaults.SearchRequestDefaults,
    pageSize: 64,
  },
  pagination: {
    centralRange: 1,
  },
  responseHandler,
  getLocalPreselection,
  searchPageUrl: '/search-results',
  page: {
    getPageType: (defaults) =>
      window.location.pathname === categorySelectionPageUrl ||
      window.location.pathname === brandSelectionPageUrl
        ? 'parts'
        : window.document.body.classList.contains('papaSupermarket-pageType--category')
          ? 'category'
          : window.document.body.classList.contains('papaSupermarket-pageType--brand')
            ? 'brand'
            : defaults.getter(),
  },
  Widgets: [
    ...bigcommerceGenericDefaults.Widgets.filter(
      (w) =>
        ![
          'CategoryPage',
          'SearchBox',
          'HeaderVehicleWidget',
          'SearchVehicleWidget',
          'VehicleWidgetDialog',
          'VerifyFitment',
        ].includes(w.name),
    ).map((w) => (w.name === 'FacetBrandTiles' ? { ...w, isVehicleWidgetDisabled: true } : w)),
    {
      name: 'SearchBox',
      location: 'header .emthemesModez-quickSearch',
      template: 'SearchBox',
    },
    {
      name: 'CategoryPage',
      type: 'SearchPage',
      location: {
        replace: ['category', 'brand']
          .map(
            (pageSelector) =>
              `body.papaSupermarket-pageType--${pageSelector} .page .page-content #product-listing-container`,
          )
          .join(),
        class: 'cm_SearchPage cm_main-content clear-self',
      },
      template: 'MainContent',
    },
    {
      name: 'CategoryFacetPanel',
      type: 'SearchPage',
      location: {
        replace: ['category', 'brand']
          .map((pageSelector) => `body.papaSupermarket-pageType--${pageSelector} .page .page-sidebar`)
          .join(),
      },
      template: 'FacetPanelContainer',
    },
    {
      name: 'SearchBoxMobile',
      type: 'SearchBox',
      location: 'body .emthemesModez-quickSearch--mobileOnly',
      template: 'SearchBox',
    },
    {
      name: 'HeaderVehicleWidget',
      type: 'VehicleWidget',
      location: '#cm-ymm',
      template: 'fitmentSearch/collapsibleVehicleWidget',
      columnBreakpoint: 650,
      initCollapsed: true,
      fields: !!window.Convermax.config?.extraFieldsAsFilters && baseFitmentFields,
      isVehicleSelectionIsolated: !!window.Convermax.config?.isVehicleSelectionIsolated,
      useSearchableDropdown: true,
      autoOpenDropdowns: 'next',
    },
    {
      name: 'SearchVehicleWidget',
      type: 'VehicleWidget',
      fields: !!window.Convermax.config?.extraFieldsAsFilters && baseFitmentFields,
      isVehicleSelectionIsolated: !!window.Convermax.config?.isVehicleSelectionIsolated,
      useSearchableDropdown: true,
      autoOpenDropdowns: 'next',
    },
    {
      name: 'VehicleWidgetDialog',
      location: { class: 'vehicle-dialog' },
      template: 'fitmentSearch/dialog',
      fields: !!window.Convermax.config?.extraFieldsAsFilters && baseFitmentFields,
      useSearchableDropdown: true,
      autoOpenDropdowns: 'next',
    },
    {
      name: 'VerifyFitment',
      location: '#cm-verify-fitment',
      template: 'fitmentSearch/collapsibleVerifyFitment',
      initCollapsed: true,
    },
    {
      name: 'RelatedProducts',
      type: 'RelatedItems',
      location: '#cm-related-products',
      template: 'product/relatedItems',
      count: 15,
      getSelection: getRelatedProductsSelection,
    },
    {
      name: 'MoreFromBrand',
      type: 'RelatedItems',
      location: '#cm-more-from-brand',
      template: 'product/relatedItems',
      count: 15,
      sort: 'total_sold:desc',
      getSelection: getBrandSelection,
    },
    {
      name: 'YouMightLike',
      type: 'RelatedItems',
      location: '#cm-you-might-like',
      template: 'product/relatedItems',
      count: 15,
      sort: '_popularity:desc',
      getSelection: getYouMightLikeSelection,
    },
  ],
};
